export interface UserAgentInfo {
	browser: string;
	version: string;
	os: string;
	deviceType: 'Desktop' | 'Mobile' | 'Tablet' | 'Other';
}

/**
 * Analiza la cadena del User Agent y extrae información clave.
 * @param userAgentString El string crudo del User Agent (ej: navigator.userAgent).
 * @returns Un objeto con información formateada.
 */
export function parseUserAgent(userAgentString: string): UserAgentInfo {
	const ua = userAgentString.toLowerCase();
	let browser = 'Unknown Browser';
	let version = 'N/A';
	let os = 'Unknown OS';
	let deviceType: UserAgentInfo['deviceType'] = 'Other';

	// 1. Detección del Sistema Operativo (OS)
	if (ua.includes('windows')) {
		os = 'Windows';
	} else if (ua.includes('android')) {
		os = 'Android';
	} else if (ua.includes('ipad') || (ua.includes('macintosh') && 'ontouchend' in document)) {
		// Manejo de iPads que a veces se reportan como Mac
		os = 'iOS/iPadOS';
	} else if (ua.includes('iphone') || ua.includes('ipod')) {
		os = 'iOS/iPhone';
	} else if (ua.includes('mac')) {
		os = 'macOS';
	} else if (ua.includes('linux')) {
		os = 'Linux';
	}

	// 2. Detección del Tipo de Dispositivo
	if (ua.includes('mobi')) {
		deviceType = 'Mobile';
	} else if (ua.includes('tablet') || ua.includes('ipad')) {
		deviceType = 'Tablet';
	} else if (os === 'Windows' || os === 'macOS' || os === 'Linux') {
		deviceType = 'Desktop';
	}

	// 3. Detección del Navegador y Versión (Orden de verificación IMPORTANTE)
	// WebViews in-app antes que Chrome (también incluyen chrome/safari en el UA)

	if (ua.includes('instagram')) {
		browser = 'Instagram';
		const match = ua.match(/instagram[\/\s]+(\d+\.\d+)/);
		if (match) version = match[1];
	} else if (ua.includes('whatsapp') || ua.includes('wa4a')) {
		browser = 'WhatsApp';
		const match = ua.match(/whatsapp[\/\s]+(\d+\.\d+)/);
		if (match) version = match[1];
	} else if (ua.includes('crios') || (ua.includes('chrome') && !ua.includes('opr/'))) {
		// CriOS = Chrome en iOS (no incluye la cadena "chrome")
		browser = 'Chrome';
		const match = ua.match(/(?:chrome|crios)\/(\d+\.\d+)/);
		if (match) version = match[1];
	}

	// Firefox / FxiOS (no pisa Instagram/WhatsApp)
	if ((ua.includes('firefox') || ua.includes('fxios')) && browser !== 'Instagram' && browser !== 'WhatsApp') {
		browser = 'Firefox';
		const match = ua.match(/(?:firefox|fxios)\/(\d+\.\d+)/);
		if (match) version = match[1];
	} else if (browser !== 'Instagram' && browser !== 'WhatsApp') {
		if (ua.includes('edgios') || ua.includes('edg/') || ua.includes('edge')) {
			// EdgiOS / Edg/ (Chromium) / Edge legacy
			browser = 'Edge';
			const match = ua.match(/(?:edgios|edg|edge)\/(\d+\.\d+)/);
			if (match) version = match[1];
		} else if (
			ua.includes('safari') &&
			!ua.includes('chrome') &&
			!ua.includes('crios') &&
			!ua.includes('fxios') &&
			!ua.includes('edgios')
		) {
			browser = 'Safari';
			const match = ua.match(/version\/(\d+\.\d+)/);
			if (match) version = match[1];
		} else if (ua.includes('opr/') || ua.includes('opera')) {
			browser = 'Opera';
			const match = ua.match(/(?:opr|opera)\/(\d+\.\d+)/);
			if (match) version = match[1];
		}
	}

	return { browser, version, os, deviceType };
}
